import { Box } from "@mantine/core";
import { IconArrowDown, IconArrowsUpDown, IconArrowUp } from "@tabler/icons-react";
import { type Header, type HeaderGroup, type RowData } from "@tanstack/react-table-v9";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";

import { Text } from "../../atoms/Typographies/Text";
import { getColumnCssSizing } from "./getColumnCssSizing";
import classes from "./Table.module.css";
import { type TableAppFeatures, useTableContext } from "./tableHook";
import { TABLE_ACTIONS_COLUMN_WIDTH, TABLE_DND_COLUMN_WIDTH } from "./TableRow";

type TableHeaderProps<TData extends RowData> = {
  headerGroups: HeaderGroup<TableAppFeatures, TData>[];
  enableRowSelection: boolean;
  enableDnd: boolean;
};

const SortIcon = ({ header }: { header: Header<TableAppFeatures, RowData> }) => {
  const sorted = header.column.getIsSorted();
  const isActive = sorted !== false;

  const iconClass = isActive ? `${classes.sortIcon!} ${classes.sortIconActive!}` : classes.sortIcon;

  if (sorted === "asc") {
    return (
      <span className={iconClass}>
        <IconArrowUp size={14} />
      </span>
    );
  }
  if (sorted === "desc") {
    return (
      <span className={iconClass}>
        <IconArrowDown size={14} />
      </span>
    );
  }
  return (
    <span className={iconClass}>
      <IconArrowsUpDown size={14} />
    </span>
  );
};

const TableHeader = <TData extends RowData>({
  headerGroups,
  enableDnd,
}: TableHeaderProps<TData>) => {
  const { t } = useTranslation("components");
  const table = useTableContext<TData>();
  const stickyOffsets = table.options.meta?.stickyOffsets;
  const lastStickyColumnId = table.options.meta?.lastStickyColumnId;

  return (
    <>
      {headerGroups.map((headerGroup) => (
        <Box key={headerGroup.id} component="tr" className={classes.headerRow}>
          {enableDnd && (
            <Box
              component="th"
              style={{
                width: TABLE_DND_COLUMN_WIDTH,
                minWidth: TABLE_DND_COLUMN_WIDTH,
                maxWidth: TABLE_DND_COLUMN_WIDTH,
                padding: 0,
                position: "sticky",
                left: 0,
                zIndex: 4,
                borderBottom: "none",
              }}
            />
          )}
          {headerGroup.headers.map((header) => {
            const isSelectCol = header.column.id === "__select__";
            const canSort = header.column.getCanSort();
            const stickyLeft = stickyOffsets?.[header.column.id];
            const isSticky = stickyLeft !== undefined;
            const sizing = getColumnCssSizing(header.column);
            const align = header.column.columnDef.meta?.align;
            const hasLabel = !!header.column.columnDef.header;

            return (
              <Box
                key={header.id}
                component="th"
                className={canSort ? `${classes.headerCell!}` : undefined}
                style={{
                  ...sizing,
                  padding: isSelectCol
                    ? `${spacing[3]} ${spacing[1]} ${spacing[3]} ${spacing[2]}`
                    : `${spacing[2]} ${spacing[3]}`,
                  whiteSpace: "nowrap",
                  ...(isSticky
                    ? {
                        position: "sticky",
                        left: stickyLeft,
                        zIndex: 3,
                        borderRight:
                          header.column.id === lastStickyColumnId
                            ? `1px solid var(--table-sticky-border-color, transparent)`
                            : undefined,
                      }
                    : { zIndex: 2 }),
                }}
                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                title={
                  canSort
                    ? header.column.getIsSorted() === "asc"
                      ? t("table.sortDescending")
                      : t("table.sortAscending")
                    : undefined
                }
              >
                {header.isPlaceholder ? null : isSelectCol ? (
                  <table.FlexRender header={header} />
                ) : (
                  <span
                    className={classes.headerCellInner}
                    style={align === "right" ? { justifyContent: "flex-end" } : undefined}
                  >
                    <span className={classes.headerCellLabel} style={{ gap: spacing[1.5] }}>
                      {header.column.columnDef.meta?.headerIcon}
                      {hasLabel && (
                        <Text
                          component="span"
                          size="small"
                          type="medium"
                          color={colors.text.primaryDefault}
                        >
                          <table.FlexRender header={header} />
                        </Text>
                      )}
                      {header.column.columnDef.meta?.optional && (
                        <Text
                          component="span"
                          size="tiny"
                          type="regular"
                          color={colors.text.secondaryDefault}
                        >
                          {t("table.optional")}
                        </Text>
                      )}
                    </span>
                    {canSort && <SortIcon header={header as Header<TableAppFeatures, RowData>} />}
                  </span>
                )}
              </Box>
            );
          })}
          {!!table.options.meta?.rowActions?.length && (
            <Box
              component="th"
              style={{
                width: TABLE_ACTIONS_COLUMN_WIDTH,
                minWidth: TABLE_ACTIONS_COLUMN_WIDTH,
                padding: 0,
                position: "sticky",
                right: 0,
                zIndex: 3,
              }}
            />
          )}
        </Box>
      ))}
    </>
  );
};

export { TableHeader };
